import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';
import { functions } from './config';
import { httpsCallable } from 'firebase/functions';

export interface PaymentInfo {
  userId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  ssnLast4: string;
  fullSsn?: string;
  phone: string;
  email: string;
  // Managed server-side
  website?: string;
  address1: string;
  city: string;
  state: string;
  zipCode: string;
  bankName: string;
  accountType: 'checking' | 'savings';
  routingNumber: string;
  accountNumber: string;
  businessType: 'individual' | 'company';
  businessName?: string;
  // Managed server-side
  businessDescription?: string;
  mccCode: string;
  // Managed server-side
  productDescription?: string;
  identificationDocument?: File;
  stripeAccountId?: string;
  verificationStatus?: 'pending' | 'pending_verification' | 'verified' | 'failed';
  createdAt: any;
  updatedAt: any;
}

//

export async function savePaymentInfo(userId: string, data: Omit<PaymentInfo, 'userId' | 'createdAt' | 'updatedAt' | 'stripeAccountId' | 'verificationStatus'>) {
  try {
    // First, get the existing Stripe account ID from stripe_accounts collection
    const stripeAccountDoc = await getDoc(doc(db, 'stripe_accounts', userId));
    const existingStripeAccountId = stripeAccountDoc.exists() ? stripeAccountDoc.data().stripeAccountId : null;
    console.log('Existing Stripe account ID from stripe_accounts:', existingStripeAccountId);

    // Create or update Stripe connected account
    const createStripeAccount = httpsCallable(functions, 'createStripeConnectedAccount');

    // Extract file from data before sending to Stripe
    const { identificationDocument, ...stripeData } = data;

    // Convert file to base64 if it exists
    let fileData;
    if (identificationDocument) {
      console.log('Converting identification document to base64');
      const reader = new FileReader();
      fileData = await new Promise((resolve, reject) => {
        reader.onload = () => {
          console.log('File successfully converted to base64');
          resolve(reader.result);
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          reject(error);
        };
        reader.readAsDataURL(identificationDocument);
      });
    }

    const stripeRequestData = {
      stripeAccountId: existingStripeAccountId,
      bankAccount: {
        accountNumber: stripeData.accountNumber,
        routingNumber: stripeData.routingNumber,
        accountType: stripeData.accountType,
        shouldUpdate: existingStripeAccountId ? true : false,
      },
      individual: {
        firstName: stripeData.firstName,
        lastName: stripeData.lastName,
        email: stripeData.email,
        phone: stripeData.phone,
        dateOfBirth: stripeData.dateOfBirth,
        ssnLast4: stripeData.ssnLast4,
        fullSsn: stripeData.fullSsn,
        address: {
          line1: stripeData.address1,
          city: stripeData.city,
          state: stripeData.state,
          postal_code: stripeData.zipCode,
          country: 'US',
        },
      },
      // business/profile fields are set server-side; only pass MCC if provided
      business: stripeData.mccCode ? { mccCode: stripeData.mccCode } : undefined,
      documents: fileData ? {
        identification: fileData,
      } : undefined,
    };

    console.log('Calling Stripe with data:', {
      ...stripeRequestData,
      bankAccount: {
        ...stripeRequestData.bankAccount,
        accountNumber: '****' + stripeData.accountNumber.slice(-4),
      },
      individual: {
        ...stripeRequestData.individual,
        ssnLast4: stripeData.ssnLast4,
      },
      documents: fileData ? { identification: '[BASE64_DATA]' } : undefined,
    });

    try {
      const stripeResult = await createStripeAccount(stripeRequestData) as any;
      console.log('Stripe API response:', stripeResult);

      // v2 callable returns flat payload
      const stripeData = (stripeResult && (stripeResult as any).data) ? (stripeResult as any).data : (stripeResult as any);
      console.log('Stripe result data:', stripeData);

      // If there are validation errors, return them immediately
      if (stripeData.status === 'validation_failed' || (stripeData.validationErrors && stripeData.validationErrors.length > 0)) {
        console.log('Validation errors from Stripe:', stripeData.validationErrors);
        return {
          userId,
          stripeAccountId: stripeData.stripeAccountId,
          verificationStatus: 'validation_failed',
          validationErrors: stripeData.validationErrors,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...data
        };
      }

      // If status is undefined or null, set it to 'pending'
      const verificationStatus = stripeData.status || 'pending';

      if (existingStripeAccountId && existingStripeAccountId !== stripeData.stripeAccountId) {
        console.error('Warning: Stripe account ID mismatch!', {
          existing: existingStripeAccountId,
          new: stripeData.stripeAccountId
        });
      }

      // Save to Firebase (without the file object)
      const paymentRef = doc(db, 'paymentinfo', userId);
      console.log('Saving to paymentinfo collection:', { userId, stripeAccountId: stripeData.stripeAccountId, status: stripeData.status });

      const { identificationDocument: _, ...paymentDataWithoutFile } = data;
      const paymentData = {
        ...paymentDataWithoutFile,
        userId,
        stripeAccountId: stripeData.stripeAccountId,
        verificationStatus,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      try {
        await setDoc(paymentRef, paymentData, { merge: true });
        console.log('Successfully saved to paymentinfo collection');
      } catch (error: any) {
        console.error('Error saving to paymentinfo:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        throw error;
      }

      // Do not write to stripe_accounts here; backend already writes it securely.

      return paymentData;
    } catch (error: any) {
      console.error('Error saving payment info:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      if (error.details) {
        console.error('Error details:', error.details);
      }

      // Return error response instead of throwing
      const { identificationDocument: _, ...dataWithoutFile } = data;
      return {
        userId,
        stripeAccountId: '',
        verificationStatus: 'validation_failed',
        validationErrors: error.details?.validationErrors || [{
          field: 'general',
          message: error.message || 'Failed to save payment information'
        }],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...dataWithoutFile
      };
    }
  } catch (error: any) {
    console.error('Error saving payment info:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    if (error.details) {
      console.error('Error details:', error.details);
    }
    throw new Error(error.message || 'Failed to save payment information');
  }
}

export async function getPaymentInfo(userId: string): Promise<PaymentInfo | null> {
  try {
    const paymentRef = doc(db, 'paymentinfo', userId);
    const paymentDoc = await getDoc(paymentRef);

    if (!paymentDoc.exists()) {
      return null;
    }

    return paymentDoc.data() as PaymentInfo;
  } catch (error) {
    console.error('[getPaymentInfo] Error fetching payment info:', error);
    throw error;
  }
}

export async function updatePaymentVerificationStatus(userId: string, status: PaymentInfo['verificationStatus']) {
  try {
    const paymentRef = doc(db, 'paymentinfo', userId);
    await updateDoc(paymentRef, {
      verificationStatus: status,
      updatedAt: serverTimestamp(),
    });
  } catch (error: any) {
    console.error('Error updating verification status:', error);
    throw new Error(error.message || 'Failed to update verification status');
  }
}

// New: start Stripe Express onboarding via callable
export async function startStripeOnboarding(country?: string): Promise<string> {
  try {
    const callable = httpsCallable(functions, 'createStripeOnboardingLink');
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
    const result = await callable({ origin, country });
    const data = (result as any)?.data || result;
    const url = (data as any)?.url;
    if (!url) {
      throw new Error('Failed to get onboarding URL');
    }
    return url as string;
  } catch (e: any) {
    const msg = e?.message || e?.code || 'Failed to start Stripe onboarding';
    const detailsMsg = e?.details?.message || e?.details || '';
    throw new Error(detailsMsg ? `${msg}: ${detailsMsg}` : msg);
  }
}

// Helper to fetch latest verification status (from paymentinfo or users)
export async function fetchStripeVerificationStatus(userId: string): Promise<'not_set' | 'pending_verification' | 'verified' | 'failed'> {
  try {
    const paymentRef = doc(db, 'paymentinfo', userId);
    const snap = await getDoc(paymentRef);
    if (snap.exists()) {
      const v = (snap.data() as any)?.verificationStatus as any;
      // Only trust explicit statuses we surface in the UI. Treat anything
      // else (including undefined or legacy 'pending') as not yet set.
      if (v === 'pending_verification' || v === 'verified' || v === 'failed') {
        return v;
      }
      // Fall through to check consolidated user stripe status or default
      // to not_set so we don't incorrectly show "being verified" too early.
    }
  } catch { }
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (userSnap.exists()) {
      const v = (userSnap.data() as any)?.stripeAccountStatus as any;
      if (v === 'pending_verification' || v === 'verified' || v === 'failed') {
        return v;
      }
      return 'not_set';
    }
  } catch { }
  return 'not_set';
}

// Sync status from Stripe via callable and return consolidated result
export interface StripeStatusResult {
  status: 'not_set' | 'pending_verification' | 'verified' | 'failed';
  issues: string[];
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requirements: {
    currently_due: string[];
    eventually_due: string[];
    pending_verification: string[];
  };
  disabledReason?: string | null;
  stripeAccountId?: string | null;
}

export async function syncStripeStatus(): Promise<StripeStatusResult> {
  const callable = httpsCallable(functions, 'syncStripeAccountStatus');
  const result = await callable({});
  const data = (result as any)?.data || result;
  return {
    status: (data as any)?.status || 'not_set',
    issues: ((data as any)?.issues || []) as string[],
    chargesEnabled: (data as any)?.chargesEnabled || false,
    payoutsEnabled: (data as any)?.payoutsEnabled || false,
    requirements: {
      currently_due: ((data as any)?.requirements?.currently_due || []) as string[],
      eventually_due: ((data as any)?.requirements?.eventually_due || []) as string[],
      pending_verification: ((data as any)?.requirements?.pending_verification || []) as string[],
    },
    disabledReason: (data as any)?.disabledReason || null,
    stripeAccountId: (data as any)?.stripeAccountId || null,
  };
}